import * as React from 'react';
import { Props } from 'react';
import { connect, Dispatch } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as queryString from 'query-string';

import { AppState } from '../../reducers';

import { setChannelUserIdAction, setUserPhoneAction, setChannelIdAction, setUserLangAction } from '../../actions/user';
import { authorizationAction, resetAuthorizationAction } from '../../actions/Authorization/authorization';

import { AuthorizationView } from './AuthorizationView';
import Spiner from '../RegistrationAuthorization/Spiner/Spiner';
import i18next from 'i18next';

interface RouteProps extends RouteComponentProps<{}> {
    match: any;
    history: any;
}

interface AuthorizationContainerProps extends Props<AuthorizationContainer> {
    authorization: boolean;
    loadingAuthorization: boolean;
    errorAuthorization: {
        messages: string[];
        code: number;
    } | null;
    metaAuthorization: {
        timeout: number;
    } | null;

    phone: string;
    lang: string;

    userId: string;
    channelUserId: string;
    channelId: string;
}

interface AuthorizationContainerDispatch extends Props<AuthorizationContainer> {
    setLang: (lang: string) => any;
    setPhone: (phone: string) => any;
    setChannelUserId: (channelUserId: string) => any;
    setChannelId: (channelId: string) => any;
    onAuthorization: (phone: string, channelUserId: string) => any;
    resetAuthorization: () => any;
}

function mapProps(state: AppState, props: AuthorizationContainerProps): AuthorizationContainerProps {
    return {
        authorization: state.authorizationState.authorization,
        loadingAuthorization: state.authorizationState.loadingAuthorization,
        errorAuthorization: state.authorizationState.errorAuthorization,
        metaAuthorization: state.authorizationState.metaAuthorization,

        phone: state.userState.phone,
        lang: state.userState.lang,

        userId: state.userState.userId,
        channelUserId: state.userState.channelUserId,
        channelId: state.userState.channelId,
    };
}

function mapDispatch(dispatch: Dispatch<any>): AuthorizationContainerDispatch {
    return {
        setLang: (lang: string) => dispatch(setUserLangAction(lang)),
        setPhone: (phone: string) => dispatch(setUserPhoneAction(phone)),
        setChannelUserId: (channelUserId: string) => dispatch(setChannelUserIdAction(channelUserId)),
        setChannelId: (channelId: string) => dispatch(setChannelIdAction(channelId)),
        onAuthorization: (phone: string, channelUserId: string) => dispatch(authorizationAction(phone, channelUserId)),
        resetAuthorization: () => dispatch(resetAuthorizationAction()),
    };
}

interface AuthorizationContainerState {
    error: string;
}

export class AuthorizationContainer extends React.Component<AuthorizationContainerProps & AuthorizationContainerDispatch & RouteProps, AuthorizationContainerState> {
    state = {
        error: '',
    };

    async componentDidMount() {
        const { history, setPhone, setChannelUserId, setChannelId, setLang, lang } = this.props;
        if (history.location.search) {
            const params = queryString.parse(history.location.search);
            const phone = params.phone;
            const channelUserId = params.user_id;
            const channel = params.channel_id;
            const error = params.error;
            const language = params.lang;
            if (!lang) {
                setLang(language || 'ru');
            }
            if (language && language !== lang && i18next.languages.some(it => it === lang)) {
                setLang(language);
                await i18next.changeLanguage(language);
            }

            if (channel) {
                setChannelId(channel);
            }
            if (phone) {
                setPhone(phone);
            }
            if (channelUserId) {
                setChannelUserId(channelUserId);
            }
            if (error) {
                this.setState({
                    error
                });
            }
        }
    }

    componentWillUnmount() {
        const {resetAuthorization} = this.props;
        resetAuthorization();
    }

    componentWillReceiveProps(nextProps: AuthorizationContainerProps & AuthorizationContainerDispatch & RouteProps) {
        const {authorization, history, phone, channelUserId, channelId, lang} = nextProps;
        if (authorization && (authorization !== this.props.authorization)) {
            history.push(`/authorization/sendCode?phone=${phone}&user_id=${encodeURIComponent(channelUserId)}&channel_id=${channelId}&lang=${lang}`);
        }
    }

    render() {
        document.title = i18next.t('authorization');
        const { setPhone, phone, onAuthorization, channelUserId, history, channelId, lang } = this.props;
        const { authorization, loadingAuthorization, errorAuthorization, metaAuthorization } = this.props;

        const bankNumber = i18next.t('bank_phone');
        const NOT_ALLOW = i18next.t('authorization_not_allowed', { phone: `<a style="color:white" href=tel:${bankNumber}>${bankNumber}</a>` });
        const BAD_LINK = i18next.t('bad_link_authorization', { phone: `<a style="color:white" href=tel:${bankNumber}>${bankNumber}</a>` });
        const CODE_TIMEOUT = (timeout: number) => i18next.t('code_timeout', { seconds: Math.ceil(timeout), phone: `<a style="color:white" href='tel:${bankNumber}'>${bankNumber}</a>` });

        let error = this.state.error;
        if (errorAuthorization) {
            if (errorAuthorization.code === 19 && metaAuthorization) {
                error = CODE_TIMEOUT(metaAuthorization.timeout);
            } else if (errorAuthorization.code === 24 || errorAuthorization.code === 0 || errorAuthorization.code === 16 || errorAuthorization.code === 7) {
                error = NOT_ALLOW;
            } else if (errorAuthorization.code === 13) {
                error = BAD_LINK;
            } else if (errorAuthorization.code === 27) {
                error = NOT_ALLOW;
            }
        }

        if (loadingAuthorization) {
            return <Spiner channelId={channelId} />;
        }

        if (errorAuthorization && !authorization && !loadingAuthorization) {
            if (errorAuthorization && errorAuthorization.code === 24) {
                history.push(`/registration/sendCode?user_id=${encodeURIComponent(channelUserId)}&channel_id=${channelId}&phone=${phone}&lang=${lang}`);
            }
            if (errorAuthorization.code === 0 || errorAuthorization.code === 7) {
                history.push(`/registration?user_id=${encodeURIComponent(channelUserId)}&phone=${phone}&channel_id=${channelId}&lang=${lang}`);
            }
        }

        return (
            <AuthorizationView
                phone={phone}
                setPhone={setPhone}
                channelId={channelId}
                onAuthorization={() => onAuthorization(phone, channelUserId)}
                error={error}
                reauthorization={localStorage.getItem('re-authorization')}
                lang={lang}
            />
        );
    }
}

export default withRouter(connect(mapProps, mapDispatch)(AuthorizationContainer));



// WEBPACK FOOTER //
// ./src/components/Authorization/AuthorizationContainer.tsx